import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DotsThreeVertical,
  Trash,
  PencilSimple,
  ArrowsLeftRight,
} from "@phosphor-icons/react";

export default function ClientCard({ client, index, onEdit, onMove, onDelete }) {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`bg-[#171717] border rounded-lg p-5 hover:border-[#333] transition-all cursor-pointer group ${
        client.is_active === false ? "border-[#262626] opacity-60" : "border-[#262626]"
      }`}
      onClick={() => navigate(`/clients/${client.id}`)}
      data-testid={`client-card-${client.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-[#007AFF]/20 flex items-center justify-center text-[#007AFF] font-bold text-sm">
              {client.first_name?.charAt(0)?.toUpperCase()}
              {client.last_name?.charAt(0)?.toUpperCase()}
            </div>
            {client.unseen_checkin_count > 0 && (
              <span
                title={`${client.unseen_checkin_count} check-in non letti`}
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#FF3B30] border-2 border-[#171717] text-white text-[9px] font-bold flex items-center justify-center leading-none animate-pulse"
                data-testid={`client-unseen-checkin-${client.id}`}
              >
                {client.unseen_checkin_count > 9 ? "9+" : client.unseen_checkin_count}
              </span>
            )}
          </div>
          <div>
            <p className="font-medium text-white group-hover:text-[#007AFF] transition-colors flex items-center gap-1.5 flex-wrap">
              <span>{client.first_name} {client.last_name}</span>
              {client.is_active === false && (
                <span
                  className="text-[9px] px-1.5 py-0.5 bg-zinc-700/40 text-zinc-400 rounded-full font-medium uppercase tracking-wider"
                  data-testid={`client-inactive-badge-${client.id}`}
                >
                  inattivo
                </span>
              )}
            </p>
            {client.email && (
              <p className="text-xs text-zinc-500">{client.email}</p>
            )}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="text-zinc-600 hover:text-zinc-300"
              onClick={(e) => e.stopPropagation()}
              data-testid={`client-menu-${client.id}`}
            >
              <DotsThreeVertical size={20} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#171717] border-[#262626]">
            <DropdownMenuItem
              className="text-white hover:bg-[#1A1A1A] cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(client);
              }}
            >
              <PencilSimple size={16} className="mr-2" /> Modifica
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-white hover:bg-[#1A1A1A] cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onMove(client);
              }}
            >
              <ArrowsLeftRight size={16} className="mr-2" /> Sposta in cartella
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-400 hover:text-red-300 focus:bg-red-500/10 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(client.id);
              }}
            >
              <Trash size={16} className="mr-2" /> Elimina
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {client.weight_kg && (
          <span className="px-2 py-1 bg-[#007AFF]/10 text-[#007AFF] rounded">
            {client.weight_kg} kg
          </span>
        )}
        {client.caloric_status && (
          <span
            className={`px-2 py-1 rounded capitalize ${
              client.caloric_status === "ipocalorica"
                ? "bg-red-500/10 text-red-400"
                : client.caloric_status === "normocalorica"
                ? "bg-[#34C759]/10 text-[#34C759]"
                : "bg-green-500/10 text-green-400"
            }`}
          >
            {client.caloric_status}
          </span>
        )}
        {client.objectives && (
          <span className="px-2 py-1 bg-zinc-700/50 text-zinc-400 rounded truncate max-w-[150px]">
            {client.objectives}
          </span>
        )}
      </div>
    </motion.div>
  );
}
